import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Ship, Wallet, FileText, BarChart3, Settings, ClipboardCheck,
  Search, Sun, Moon, MonitorSmartphone, ChevronLeft, ChevronRight, LogOut, Globe, Truck, AtSign,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LANGUAGES } from '../i18n';
import CommandPalette from './CommandPalette';

const NAV = [
  { key: 'dashboard', to: '/erp',           icon: LayoutDashboard, i18n: 'nav.dashboard',   fallback: 'Dashboard' },
  { key: 'customers', to: '/erp/customers', icon: Users,            i18n: 'nav.customers',   fallback: 'Customers' },
  { key: 'shipments', to: '/erp/shipments', icon: Ship,             i18n: 'nav.shipments',   fallback: 'Shipments' },
  { key: 'tracking',  to: '/erp/tracking',  icon: Truck,            i18n: 'nav.tracking',    fallback: 'Tracking' },
  { key: 'approvals', to: '/erp/approvals', icon: ClipboardCheck,   i18n: 'nav.approvals',   fallback: 'Approvals' },
  { key: 'emails',    to: '/erp/emails',    icon: AtSign,           i18n: 'nav.emails',      fallback: 'Email Directory' },
  { key: 'finance',   to: '/erp/finance',   icon: Wallet,           i18n: 'nav.finance',     fallback: 'Finance' },
  { key: 'reports',   to: '/erp/reports',   icon: BarChart3,        i18n: 'nav.reports',     fallback: 'Reports' },
  { key: 'settings',  to: '/erp/settings',  icon: Settings,         i18n: 'nav.settings',    fallback: 'Settings' },
];

const roleBadge = (role) => {
  if (role === 'ADMIN') return { label: 'Owner', color: '#C9A227' };
  if (role === 'ACCOUNTANT') return { label: 'Accountant', color: '#007AFF' };
  return { label: 'Employee', color: '#34C759' };
};

/**
 * Resolve theme following user preference: 'system' | 'light' | 'dark'
 */
function useResolvedTheme() {
  const [pref, setPref] = useState(() => {
    try { return localStorage.getItem('gg_theme_pref') || 'system'; } catch (_e) { return 'system'; }
  });
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const compute = () => {
      const resolved = pref === 'system' ? mq.matches : pref === 'dark';
      setIsDark(resolved);
      document.documentElement.classList.toggle('dark', resolved);
    };
    compute();
    mq.addEventListener('change', compute);
    return () => mq.removeEventListener('change', compute);
  }, [pref]);

  const setPreference = (p) => {
    setPref(p);
    try { localStorage.setItem('gg_theme_pref', p); } catch (_e) { /* ignore */ }
  };

  return { pref, isDark, setPreference };
}

export default function ErpShell({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeMenuRef = useRef(null);
  const langMenuRef = useRef(null);
  const { pref: themePref, isDark, setPreference } = useResolvedTheme();

  useEffect(() => {
    document.documentElement.setAttribute('data-shell', 'erp');
    return () => document.documentElement.removeAttribute('data-shell');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click-outside for dropdowns
  useEffect(() => {
    const onClick = (e) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target)) setThemeMenuOpen(false);
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const badge = roleBadge(user?.role);
  const asideWidth = collapsed ? 76 : 264;
  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const themeIcon = themePref === 'system' ? <MonitorSmartphone size={15} /> : (isDark ? <Moon size={15} /> : <Sun size={15} />);

  return (
    <div className="min-h-screen flex relative" data-testid="erp-shell">
      {/* Sidebar */}
      <aside
        className="shrink-0 flex flex-col transition-[width] duration-300 ease-out relative z-10"
        style={{
          width: asideWidth,
          background: 'var(--gg-sidebar)',
          color: 'var(--gg-sidebar-text)',
          backdropFilter: 'var(--gg-blur-strong)',
          WebkitBackdropFilter: 'var(--gg-blur-strong)',
          borderInlineEnd: '1px solid var(--gg-border)',
          position: 'sticky', top: 0, height: '100vh',
        }}
        data-testid="erp-sidebar"
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-5" style={{ borderBottom: '1px solid var(--gg-border)' }}>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
            style={{
              background: '#FFFFFF',
              boxShadow: '0 6px 18px -6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            <img src="/gerizim-logo.png" alt="Gerizim" className="w-9 h-9 object-contain" />
          </div>
          {!collapsed && (
            <div className="leading-tight overflow-hidden">
              <p className="gg-display truncate" style={{ fontSize: 17, color: 'var(--gg-sidebar-text)', fontWeight: 700, letterSpacing: '0.06em' }}>
                GERIZIM
              </p>
              <p className="gg-eyebrow" style={{ fontSize: 9, color: '#C9A227' }}>Customs Clearance</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="erp-nav">
          {NAV.map((n) => {
            const active = location.pathname === n.to || (n.to !== '/erp' && location.pathname.startsWith(n.to));
            const Icon = n.icon;
            return (
              <Link
                key={n.key}
                to={n.to}
                data-active={active}
                className="gg-sidebar-item"
                data-testid={`erp-nav-${n.key}`}
                title={collapsed ? t(n.i18n, n.fallback) : ''}
              >
                <Icon size={17} className="shrink-0" />
                {!collapsed && <span className="truncate">{t(n.i18n, n.fallback)}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-3" style={{ borderTop: '1px solid var(--gg-border)' }}>
          <div
            className="rounded-2xl px-2.5 py-2.5 flex items-center gap-2.5"
            style={{ background: 'var(--gg-surface-2)' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold shrink-0"
              style={{
                background: `linear-gradient(135deg, ${badge.color}, ${badge.color}CC)`,
                color: '#fff',
                boxShadow: `0 4px 12px -4px ${badge.color}80`,
              }}
            >
              {(user?.name || '?').charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--gg-text)' }}>{user?.name || 'User'}</p>
                <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: badge.color }}>{badge.label}</p>
              </div>
            )}
            {!collapsed && (
              <button
                onClick={() => { logout(); navigate('/login'); }}
                className="p-1.5 rounded-lg"
                style={{ color: 'var(--gg-text-dim)' }}
                title="Log out"
                data-testid="erp-logout"
              >
                <LogOut size={14} />
              </button>
            )}
          </div>
          <button
            onClick={() => setCollapsed((s) => !s)}
            className="w-full mt-2 flex items-center justify-center py-1.5 rounded-xl text-[11px] transition-colors"
            style={{ background: 'transparent', color: 'var(--gg-sidebar-dim)' }}
            data-testid="erp-collapse-btn"
          >
            {collapsed ? <ChevronRight size={13} /> : <><ChevronLeft size={13} /><span className="ml-1">Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 flex items-center gap-3 px-6 py-3"
          style={{
            background: 'var(--gg-topbar)',
            backdropFilter: 'var(--gg-blur-strong)',
            WebkitBackdropFilter: 'var(--gg-blur-strong)',
            borderBottom: '1px solid var(--gg-border)',
          }}
        >
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl flex-1 max-w-lg transition-all hover:brightness-105"
            style={{
              background: 'var(--gg-surface-2)',
              border: '1px solid var(--gg-border)',
              color: 'var(--gg-text-secondary)',
            }}
            data-testid="erp-search-trigger"
          >
            <Search size={14} />
            <span className="text-[13px] flex-1 text-start">{t('common.search', 'Search customers, shipments, invoices…')}</span>
            <span
              className="text-[10px] px-2 py-0.5 rounded-md font-bold"
              style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}
            >⌘K</span>
          </button>
          <div className="flex-1" />

          {/* Language switch */}
          <div className="relative" ref={langMenuRef}>
            <button onClick={() => setLangOpen((s) => !s)} className="gg-btn-ghost" data-testid="erp-lang-btn">
              <Globe size={15} /> <span className="uppercase text-[11px] font-bold">{currentLang.code}</span>
            </button>
            {langOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="absolute end-0 mt-1 rounded-2xl p-1 min-w-[180px] z-30"
                style={{
                  background: 'var(--gg-card-solid)',
                  border: '1px solid var(--gg-border)',
                  boxShadow: 'var(--gg-shadow-elev)',
                  backdropFilter: 'var(--gg-blur)',
                }}
              >
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                    className="w-full text-start px-3 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors"
                    style={{
                      color: currentLang.code === l.code ? 'var(--gg-accent)' : 'var(--gg-text)',
                      background: currentLang.code === l.code ? 'var(--gg-accent-soft)' : 'transparent',
                    }}
                    data-testid={`erp-lang-${l.code}`}
                  >
                    <span>{l.flag}</span> <span>{l.name}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </div>

          {/* Theme switcher */}
          <div className="relative" ref={themeMenuRef}>
            <button
              onClick={() => setThemeMenuOpen((s) => !s)}
              className="gg-btn-ghost"
              data-testid="erp-theme-btn"
              title="Theme"
            >
              {themeIcon}
            </button>
            {themeMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="absolute end-0 mt-1 rounded-2xl p-1 min-w-[160px] z-30"
                style={{
                  background: 'var(--gg-card-solid)',
                  border: '1px solid var(--gg-border)',
                  boxShadow: 'var(--gg-shadow-elev)',
                }}
              >
                {[
                  { key: 'light', label: 'Light', Icon: Sun },
                  { key: 'dark',  label: 'Dark',  Icon: Moon },
                  { key: 'system', label: 'System', Icon: MonitorSmartphone },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => { setPreference(key); setThemeMenuOpen(false); }}
                    className="w-full text-start px-3 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors"
                    style={{
                      color: themePref === key ? 'var(--gg-accent)' : 'var(--gg-text)',
                      background: themePref === key ? 'var(--gg-accent-soft)' : 'transparent',
                    }}
                    data-testid={`erp-theme-${key}`}
                  >
                    <Icon size={14} /> <span>{label}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </header>

        {/* Page slot */}
        <main className="flex-1 p-6 lg:p-10 overflow-x-auto relative">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
            className="max-w-[1600px] mx-auto"
            data-page-title={title}
          >
            {children}
          </motion.div>
        </main>
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </div>
  );
}
